import React from 'react';

import { FormControlLabel, Grid, IconButton, Radio, RadioGroup } from '@material-ui/core';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { ExpandLess } from '@material-ui/icons';
import { TextField, Typography } from 'embeds/components';
import { debounce, isNaN } from 'lodash';

import CustomNumberInput from 'screens/MissionControl/Utilities/PatientBillingPage/components/BillingItems/components/BillingItemForm/components/CustomNumberInput';

import { useFiltersContext, useTabContext } from '../context/FixedFiltersContext';

import styles from './FiltersBillingAmount.useStyles';

const FILTER_KEY_NAME = 'billingAmount';

export enum AmountOptionsEnum {
  Any = 'any',
  SpecificAmount = 'specificAmount',
  CustomRange = 'custom',
}

const getAmountValue = (amountOption: string) => {
  switch (amountOption) {
    case AmountOptionsEnum.SpecificAmount:
      return {
        amount: 0,
        minAmount: null,
        maxAmount: null,
      };
    case AmountOptionsEnum.CustomRange:
      return {
        amount: null,
        minAmount: 0,
        maxAmount: 9999,
      };
    case AmountOptionsEnum.Any:
    default:
      return {
        amount: null,
        minAmount: null,
        maxAmount: null,
      };
  }
};

export interface FiltersBillingAmountProps extends WithStyles<typeof styles> {}

const FiltersBillingAmount: React.FC<FiltersBillingAmountProps> = ({ classes }) => {
  const { filters, updater } = useFiltersContext();
  const { changeTab } = useTabContext();
  const amountFilter = filters[FILTER_KEY_NAME];

  const handleRadioClick = (value: string) => {
    updater(FILTER_KEY_NAME)({
      selectedAmount: value,
      ...getAmountValue(value),
    });
  };

  const handleAmountChange = debounce((value: string, field: string) => {
    if (!isNaN(parseFloat(value))) {
      updater(FILTER_KEY_NAME)({ ...amountFilter, [field]: value });
    }
  }, 500);

  return (
    <>
      <Grid container justify="space-between" alignItems="center">
        <Grid item>
          <RadioGroup
            className={classes.radioGroup}
            name="spacing"
            aria-label="Spacing"
            value={amountFilter.selectedAmount}
            onChange={(_, value) => handleRadioClick(value)}
            row
          >
            <FormControlLabel
              classes={{ label: classes.radio, root: classes.padding }}
              value={AmountOptionsEnum.Any}
              control={<Radio color="primary" />}
              label={
                <Typography color="primary" variant="supportW500F14">
                  Any
                </Typography>
              }
            />
            <FormControlLabel
              classes={{ label: classes.radio, root: classes.padding }}
              value={AmountOptionsEnum.SpecificAmount}
              control={<Radio color="primary" />}
              label={
                <Typography color="primary" variant="supportW500F14">
                  Specific Amount
                </Typography>
              }
            />
            <FormControlLabel
              classes={{ label: classes.radio, root: classes.padding }}
              value={AmountOptionsEnum.CustomRange}
              control={<Radio color="primary" />}
              label={
                <Typography color="primary" variant="supportW500F14">
                  Custom Range
                </Typography>
              }
            />
          </RadioGroup>
        </Grid>
        <Grid item>
          <IconButton onClick={() => changeTab({ tab: null, index: false })}>
            <ExpandLess />
          </IconButton>
        </Grid>
      </Grid>
      {amountFilter.selectedAmount === AmountOptionsEnum.SpecificAmount && (
        <div className={classes.customRange}>
          <div className={classes.textFieldWrapper}>
            <TextField
              defaultValue={amountFilter.amount || 0}
              fullWidth
              InputProps={{
                classes: {
                  input: classes.textFieldInput,
                },
                // @ts-ignore
                inputComponent: CustomNumberInput,
                inputProps: {
                  maxLength: 12,
                },
              }}
              onChange={(event) => handleAmountChange(event.target.value, 'amount')}
              placeholder="$0.00"
            />
          </div>
        </div>
      )}
      {amountFilter.selectedAmount === AmountOptionsEnum.CustomRange && (
        <div className={classes.customRange}>
          <div className={classes.textFieldWrapper}>
            <TextField
              defaultValue={amountFilter.minAmount || 0}
              fullWidth
              InputProps={{
                classes: {
                  input: classes.textFieldInput,
                },
                // @ts-ignore
                inputComponent: CustomNumberInput,
                inputProps: {
                  maxLength: 12,
                },
              }}
              onChange={(event) => handleAmountChange(event.target.value, 'minAmount')}
              placeholder="$0.00"
            />
          </div>
          <div className={classes.rangeTextWrapper}>
            <Typography variant="body2">to</Typography>
          </div>
          <div className={classes.textFieldWrapper}>
            <TextField
              defaultValue={amountFilter.maxAmount || 999}
              fullWidth
              InputProps={{
                classes: {
                  input: classes.textFieldInput,
                },
                // @ts-ignore
                inputComponent: CustomNumberInput,
                inputProps: {
                  maxLength: 12,
                },
              }}
              onChange={(event) => handleAmountChange(event.target.value, 'maxAmount')}
              placeholder="$0.00"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default withStyles(styles)(FiltersBillingAmount);
